// Response Detail — pure helpers that turn a stored response + its survey's
// question definitions into human-readable sections. Global (any VOC/VOX
// client): everything comes from question types, labels and answer data.
import { cleanQuestionLabel } from '@/lib/answerFormatting';

const SKIP_TYPES = new Set(['survey_access_code', 'thank_you', 'section_header', 'rich_text_block', 'image_display']);
const FEEDBACK_TYPES = new Set(['voice', 'long_text', 'short_text', 'media_question']);
const RATING_TYPES = new Set(['attribute_ratings', 'star_rating', 'rating_scale', 'emoji_rating']);
const CUSTOMER_TYPES = new Set(['mobile_or_email', 'contact_preference', 'contact_info']);

const TYPE_LABELS = {
  nps: 'Net Promoter® Score', star_rating: 'Star Rating', rating_scale: 'Rating Scale', yes_no: 'Yes or No',
  single_choice: 'Single Choice', multiple_choice: 'Multiple Choice', short_text: 'Short Text', long_text: 'Long Text',
  number_input: 'Number', voice: 'Feedback', mobile_or_email: 'Mobile or Email', contact_preference: 'Contact Preference',
  contact_info: 'Contact Information', attribute_ratings: 'Ratings', welcome: 'Consent', confirmation: 'Confirmation',
};

export const questionLabel = (q, fallbackType) => {
  const plain = cleanQuestionLabel(q?.text);
  if (plain) return plain;
  const t = q?.type || fallbackType;
  return TYPE_LABELS[t] || (t ? t.replace(/_/g, ' ') : 'Question');
};

const humanKey = (k) => String(k).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const yesNo = (b) => (b ? 'Yes' : 'No');
const titleCase = (s) => (typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Never JSON: every value becomes a readable string.
export function readable(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'boolean') return yesNo(v);
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(readable).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    if ('consent_given' in v) return yesNo(!!v.consent_given);
    if (v.choice) return [titleCase(v.choice), v.name].filter(Boolean).join(' · ');
    for (const k of ['label', 'text', 'name', 'item']) if (typeof v[k] === 'string' && v[k]) return v[k];
    if ('value' in v) return readable(v.value);
    return Object.entries(v)
      .filter(([k, x]) => x != null && x !== '' && !['audio_url', 'blob', 'score_value', 'comment'].includes(k))
      .map(([k, x]) => `${humanKey(k)}: ${readable(x)}`).join(' · ');
  }
  return String(v);
}

export const isVoiceAnswer = (v) => !!v && typeof v === 'object' && !Array.isArray(v) && !!(v.audio_url || v.duration != null);
export const isFileList = (v) => Array.isArray(v) && v.length > 0 && v.every((x) => x && typeof x === 'object' && typeof x.url === 'string');

export function voiceTranscript(v, derived) {
  if (!isVoiceAnswer(v)) return null;
  for (const k of ['text', 'transcription']) if (typeof v[k] === 'string' && v[k].trim()) return v[k];
  return derived || null;
}

// Voice answers whose transcript was masked → their recording needs a playback warning.
export function profaneVoiceQids(response) {
  if (!response?.has_profanity) return new Set();
  const fromAnswers = Object.entries(response.masked_answers || {})
    .filter(([, m]) => m && typeof m === 'object' && !Array.isArray(m) && typeof m.text === 'string').map(([k]) => k);
  return new Set([...Object.keys(response.masked_transcripts || {}), ...fromAnswers]);
}

// Server-made transcripts shown to users: masked copy wins unless an admin chose "Show original".
export function displayTranscripts(response, showOriginal) {
  const original = response?.derived_transcripts || {};
  if (!response?.has_profanity || showOriginal) return original;
  return { ...original, ...(response.masked_transcripts || {}) };
}

// Profanity masking mirrors the stored masked_answers shape.
export function maskedValue(response, qid, val, showOriginal) {
  if (!response?.has_profanity || showOriginal) return val;
  const m = (response.masked_answers || {})[qid];
  if (m === undefined) return val;
  if (typeof m === 'string') return typeof val === 'object' && val ? { ...val, text: m } : m;
  if (Array.isArray(m) && Array.isArray(val)) return val.map((f, i) => (m[i] && typeof m[i].comment === 'string' ? { ...f, comment: m[i].comment } : f));
  if (m && typeof m === 'object' && val && typeof val === 'object') return { ...val, ...m };
  return val;
}

export const commentOf = (v) => (v && typeof v === 'object' && !Array.isArray(v) && typeof v.comment === 'string' && v.comment.trim() ? v.comment.trim() : null);

// Split answers into the Response Detail sections, in survey order.
export function buildSections(response, questions, classifications) {
  const answers = response?.answers || {};
  const followupAnswers = response?.followup_answers || {};
  const byId = Object.fromEntries((questions || []).map((q) => [q.id, q]));
  const classByQ = Object.fromEntries((classifications || []).map((c) => [c.question_id, c]));
  const out = { npsQid: null, feedback: [], ratings: [], other: [], customer: [], consent: [], classification: [] };
  const ordered = [...(questions || []).map((q) => q.id).filter((id) => id in answers), ...Object.keys(answers).filter((id) => !byId[id])];
  let parentClass = null;
  ordered.forEach((qid) => {
    const q = byId[qid] || { id: qid };
    const val = answers[qid];
    const item = { qid, q, val, followup: followupAnswers[qid] || null };
    if (!q.inline_with_prev) parentClass = null;
    if (val == null || val === '' || SKIP_TYPES.has(q.type)) return;
    const cfg = q.classification_settings;
    if (cfg?.enabled) {
      parentClass = { ...item, name: cfg.name, derived: classByQ[qid]?.label || null, children: [] };
      out.classification.push(parentClass);
      return;
    }
    if (q.inline_with_prev && parentClass) { parentClass.children.push(item); return; }
    if (q.type === 'nps' && !out.npsQid) { out.npsQid = qid; return; }
    if (q.type === 'welcome' && val && typeof val === 'object' && 'consent_given' in val) { out.consent.push({ ...item, label: 'Consent given' }); return; }
    if (q.type === 'confirmation') { out.consent.push({ ...item, label: questionLabel(q) }); return; }
    if (CUSTOMER_TYPES.has(q.type)) { out.customer.push(item); return; }
    if (FEEDBACK_TYPES.has(q.type) || (!q.type && (typeof val === 'string' || isVoiceAnswer(val)))) { out.feedback.push(item); return; }
    if (RATING_TYPES.has(q.type)) { out.ratings.push(item); return; }
    out.other.push(item);
  });
  return out;
}
